import AsyncStorage from "@react-native-async-storage/async-storage";
import { useCallback, useEffect, useState } from "react";
import type { FormId, FormValues } from "./formTypes";
import { draftStorageKey, serializeDraft } from "./formValues";
import { parseFormValues } from "./parseForm";

/**
 * A card's unsubmitted values, kept on the device per session and card.
 *
 * Client-side on purpose: a half-filled form is not a fact about the campaign, so it
 * never reaches the server. It exists so that a reload, a navigation away, or a tab
 * that sleeps does not cost the person the sentence they just wrote.
 *
 * `saved` is `null` until the read finishes, and `{}` when there is nothing to
 * restore — so the card can tell "still loading" from "nothing saved". Storage
 * failures (private mode, quota) are swallowed: the draft is a convenience, and a form
 * that cannot remember must still be a form that works.
 */
const useFormDraft = (sessionId: string, formId: FormId, enabled: boolean) => {
  const key = draftStorageKey(sessionId, formId);
  const [saved, setSaved] = useState<FormValues | null>(null);

  useEffect(() => {
    if (!enabled) {
      setSaved({});
      return;
    }
    let cancelled = false;
    AsyncStorage.getItem(key)
      .then((raw) => {
        if (!cancelled) setSaved(parseFormValues(raw));
      })
      .catch(() => {
        if (!cancelled) setSaved({});
      });
    return () => {
      cancelled = true;
    };
  }, [key, enabled]);

  const save = useCallback(
    (values: FormValues) => {
      if (!enabled) return;
      AsyncStorage.setItem(key, serializeDraft(values)).catch(() => {});
    },
    [key, enabled],
  );

  const clear = useCallback(() => {
    AsyncStorage.removeItem(key).catch(() => {});
  }, [key]);

  return { saved, save, clear };
};

export default useFormDraft;
